import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, ArrowLeft, FileText, Users, Crown, Mail, CheckCircle2 } from 'lucide-react';

export const Support = () => {
  return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-900 text-surface-900 dark:text-gray-100 font-sans pb-20">
      <nav className="border-b border-gray-200 dark:border-surface-800 bg-white dark:bg-surface-800 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Link to="/" className="flex items-center gap-2 text-surface-600 hover:text-primary transition-colors">
              <ArrowLeft size={20} />
              <span className="font-medium">Volver al inicio</span>
            </Link>
            <div className="flex items-center gap-2">
              <Zap className="h-6 w-6 text-primary" fill="currentColor" />
              <span className="font-bold text-xl tracking-tight text-surface-900 dark:text-white">Quinvoice</span>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 mt-12">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-extrabold text-primary mb-3">Centro de Ayuda</h1>
          <p className="text-lg text-surface-500">Aprende a gestionar tus facturas y potenciar tu negocio con Quinvoice.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Facturación */}
          <div className="bg-white dark:bg-surface-800 rounded-2xl p-6 shadow-sm border border-surface-200 dark:border-surface-700">
            <div className="flex items-center mb-4">
              <div className="bg-primary/10 text-primary rounded-xl p-3 me-3">
                <FileText size={24} />
              </div>
              <h2 className="text-xl font-bold mb-0">Gestión de Facturas</h2>
            </div>
            <p className="text-surface-500 mb-4">¿Cómo crear y gestionar tus documentos?</p>
            <ul className="space-y-3 text-sm text-surface-700 dark:text-surface-300">
              <li className="flex items-start gap-2">
                <CheckCircle2 size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                <span><strong>Nueva Factura:</strong> Ve a "Facturas" y haz clic en el botón de agregar. Puedes crear un cliente nuevo directamente desde allí.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                <span><strong>Estados:</strong> Las facturas pueden estar en estado "Abierta", "Pagada" o "Cerrada".</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                <span><strong>Pagos:</strong> Dentro del editor de facturas, puedes registrar pagos parciales hasta cubrir el total.</span>
              </li>
            </ul>
          </div>

          {/* Clientes y Proyectos */}
          <div className="bg-white dark:bg-surface-800 rounded-2xl p-6 shadow-sm border border-surface-200 dark:border-surface-700">
            <div className="flex items-center mb-4">
              <div className="bg-green-500/10 text-green-500 rounded-xl p-3 me-3">
                <Users size={24} />
              </div>
              <h2 className="text-xl font-bold mb-0">Clientes y Proyectos</h2>
            </div>
            <p className="text-surface-500 mb-4">Organiza tu flujo de trabajo.</p>
            <ul className="space-y-3 text-sm text-surface-700 dark:text-surface-300">
              <li className="flex items-start gap-2">
                <CheckCircle2 size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                <span><strong>Clientes:</strong> Registra la información de contacto y direcciones para que aparezcan automáticamente en tus PDFs.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                <span><strong>Proyectos:</strong> Agrupa tus facturas por proyectos para llevar un control detallado del trabajo realizado para un mismo cliente.</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Suscripciones */}
        <div className="bg-surface-100 dark:bg-surface-800/50 rounded-2xl p-8 mb-10 shadow-sm border border-surface-200 dark:border-surface-700">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-1">
              <h3 className="text-xl font-bold flex items-center gap-2 mb-3">
                <Crown size={24} className="text-yellow-500" />
                Planes y Límites
              </h3>
              <p className="text-surface-700 dark:text-surface-300 leading-relaxed">
                El <strong>Plan Gratuito</strong> te permite crear 1 cliente, 1 proyecto, 5 presupuestos y 5 facturas. Para tener acceso ilimitado, historial de registros y soporte prioritario, puedes suscribirte a <strong>Pro</strong> por solo $5.99/mes.
              </p>
            </div>
            <div className="shrink-0">
              <Link to="/register" className="inline-block px-6 py-3 bg-primary hover:bg-primary-hover text-white rounded-xl font-semibold shadow-md transition-colors">
                Crear una Cuenta
              </Link>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mb-10">
          <h3 className="text-2xl font-bold text-center mb-6">Preguntas Frecuentes</h3>
          <div className="bg-white dark:bg-surface-800 rounded-2xl shadow-sm border border-surface-200 dark:border-surface-700 overflow-hidden divide-y divide-surface-200 dark:divide-surface-700">
            <div className="p-6">
              <h4 className="font-bold text-lg mb-2">¿Cómo descargo mi factura en PDF?</h4>
              <p className="text-surface-600 dark:text-surface-400">En la lista de facturas, encontrarás un icono de impresora o la opción "Imprimir". Esto generará automáticamente un PDF profesional listo para enviar.</p>
            </div>
            <div className="p-6">
              <h4 className="font-bold text-lg mb-2">¿Puedo cambiar mi contraseña?</h4>
              <p className="text-surface-600 dark:text-surface-400">Sí, ve a tu Perfil y selecciona la opción "Cambiar Contraseña" en el menú lateral o de cuenta.</p>
            </div>
          </div>
        </div>

        {/* Contact Support */}
        <div className="text-center p-8 bg-white dark:bg-surface-800 rounded-2xl shadow-sm border border-surface-200 dark:border-surface-700">
          <h4 className="text-xl font-bold mb-2">¿Aún tienes dudas?</h4>
          <p className="text-surface-500 mb-6">Estamos aquí para ayudarte. Contáctanos directamente.</p>
          <a href="mailto:dunicode@gmail.com" className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-primary text-primary hover:bg-primary hover:text-white rounded-xl font-bold transition-colors">
            <Mail size={20} />
            Enviar email a soporte
          </a>
        </div>
      </div>
    </div>
  );
};
